import { Pagination } from '@/components/commons';
import { FamilyAdminService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import {
  FamilyListSortBy,
  IAdminFamilyRow,
  IFamilyGameAccess,
} from '@/types/response';
import { Button, Card, Form, Input, Table, Tag, Tooltip, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTER } from '@/routes/routes';

const { Title, Paragraph, Text } = Typography;

interface IProps {
  title?: string;
}

type Filters = {
  page: number;
  limit: number;
  name?: string;
  sortBy: FamilyListSortBy;
  sortDirection: 'asc' | 'desc';
};

const fmtDate = (v: string | null) => (v ? String(v).slice(0, 10) : '-');

const GameAccessTag: React.FC<{ access: IFamilyGameAccess }> = ({ access }) => {
  if (access.source === 'package') {
    return (
      <Tooltip
        title={
          access.packageEndAt ? `期限: ${fmtDate(access.packageEndAt)}` : undefined
        }
      >
        <Tag color='green'>課金</Tag>
      </Tooltip>
    );
  }
  if (access.source === 'invite') return <Tag color='blue'>企業</Tag>;

  return <Text type='secondary'>-</Text>;
};

export const FamilyList: React.FC<IProps> = ({ title }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({
    page: 1,
    limit: 20,
    sortBy: 'lastFamilyTalkAt',
    sortDirection: 'desc',
  });
  const [nameInput, setNameInput] = useState('');

  // 名前検索は入力途中でクエリが走らないよう 500ms デバウンス
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        name: nameInput.trim() || undefined,
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [nameInput]);

  const { data, isLoading } = useQuery(
    [ENDPOINT.FAMILY.LIST, filters],
    () => FamilyAdminService.listFamilies(filters),
    { keepPreviousData: true },
  );
  const families = data?.data.data ?? [];
  const total = data?.data.pagination.total ?? 0;

  const sortableColumn = (key: FamilyListSortBy) => ({
    sorter: true,
    sortOrder:
      filters.sortBy === key
        ? filters.sortDirection === 'asc'
          ? ('ascend' as const)
          : ('descend' as const)
        : undefined,
  });

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            家族一覧
          </Title>
          <Paragraph type='secondary' style={{ marginBottom: 16 }}>
            アクティブな家族の把握用の一覧です。既定は最終家族トーク日（ユーザー発言のみ・Bot除く）の降順。
            最終投稿はBot・自動投稿（歩数レポート等）を除いた人の公開投稿の最新日です。
            「実行」ボタンでその家族を対象に AIエージェントを起動できます。
          </Paragraph>
        </header>

        <Card>
          <Form layout='inline'>
            <Form.Item label='家族名'>
              <Input
                style={{ width: 240 }}
                allowClear
                placeholder='部分一致で検索'
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Card>

        <Card>
          <Table
            rowKey='id'
            size='small'
            loading={isLoading}
            dataSource={families}
            pagination={false}
            scroll={{ x: 1200 }}
            onChange={(_p, _f, sorter) => {
              const s = Array.isArray(sorter) ? sorter[0] : sorter;
              if (!s?.columnKey) return;
              setFilters((prev) => ({
                ...prev,
                page: 1,
                sortBy: s.columnKey as FamilyListSortBy,
                sortDirection: s.order === 'ascend' ? 'asc' : 'desc',
              }));
            }}
            columns={[
              {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
                width: 70,
                fixed: 'left',
                ...sortableColumn('id'),
              },
              {
                title: '名前',
                dataIndex: 'name',
                key: 'name',
                width: 200,
                fixed: 'left',
                ellipsis: true,
                render: (v: string, r: IAdminFamilyRow) => (
                  <span>
                    {v}
                    {r.botName && r.botName !== 'フクマル' && (
                      <Tooltip title={`Bot名: ${r.botName}`}>
                        <Tag style={{ marginLeft: 4 }} color='gold'>
                          Bot改名
                        </Tag>
                      </Tooltip>
                    )}
                  </span>
                ),
              },
              {
                title: 'メンバー',
                dataIndex: 'memberCount',
                key: 'memberCount',
                width: 100,
                ...sortableColumn('memberCount'),
                render: (v: number, r: IAdminFamilyRow) => (
                  <span>
                    {v}人
                    {r.pendingMemberCount > 0 && (
                      <Text type='secondary' style={{ fontSize: 11 }}>
                        {' '}
                        (+招待中{r.pendingMemberCount})
                      </Text>
                    )}
                  </span>
                ),
              },
              {
                title: '作成日',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 110,
                ...sortableColumn('createdAt'),
                render: fmtDate,
              },
              {
                title: '最終投稿',
                dataIndex: 'lastPostAt',
                key: 'lastPostAt',
                width: 110,
                ...sortableColumn('lastPostAt'),
                render: fmtDate,
              },
              {
                title: '最終トーク',
                dataIndex: 'lastFamilyTalkAt',
                key: 'lastFamilyTalkAt',
                width: 110,
                ...sortableColumn('lastFamilyTalkAt'),
                render: fmtDate,
              },
              {
                title: '最終利用',
                dataIndex: 'lastActiveAt',
                key: 'lastActiveAt',
                width: 110,
                ...sortableColumn('lastActiveAt'),
                render: fmtDate,
              },
              {
                title: 'CogEvo',
                dataIndex: 'cogevo',
                width: 90,
                render: (v: IFamilyGameAccess) => <GameAccessTag access={v} />,
              },
              {
                title: 'のうKNOW',
                dataIndex: 'nouknow',
                width: 90,
                render: (v: IFamilyGameAccess) => <GameAccessTag access={v} />,
              },
              {
                title: '企業コード',
                dataIndex: 'companyCodes',
                width: 160,
                ellipsis: true,
                render: (v: string | null, r: IAdminFamilyRow) =>
                  v ? (
                    <Tooltip title={r.companyNames ?? undefined}>
                      <span>{v}</span>
                    </Tooltip>
                  ) : (
                    '-'
                  ),
              },
              {
                title: '',
                key: 'actions',
                width: 110,
                fixed: 'right',
                render: (_: unknown, r: IAdminFamilyRow) => (
                  <Button
                    size='small'
                    icon={<ThunderboltOutlined />}
                    onClick={() =>
                      navigate(`${APP_ROUTER.AGENT.RUN}?familyId=${r.id}`)
                    }
                  >
                    実行
                  </Button>
                ),
              },
            ]}
          />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              total={total}
              pageSize={filters.limit}
              current={filters.page}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(page: number, pageSize: number) =>
                setFilters((prev) => ({ ...prev, page, limit: pageSize }))
              }
              onShowSizeChange={(page: number, pageSize: number) =>
                setFilters((prev) => ({ ...prev, page, limit: pageSize }))
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
};
